import React, { useState } from 'react';
import { Smile, Phone, Instagram, Facebook, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

/**
 * Header component - Navigation and contact info
 */
const Header = ({ onBooking, onServicesScroll, phoneLink, trackPhoneClick }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
      {/* Top bar with phone and social media */}
      <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 border-b border-sky-gray/20">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Phone Number */}
            <a 
              href={phoneLink}
              onClick={trackPhoneClick}
              className="flex items-center gap-2 text-sm md:text-base text-rabbit hover:text-burnt-coral transition-colors group"
              aria-label="Call us at 07787 814 064"
            >
              <Phone className="w-4 h-4 md:w-5 md:h-5 text-burnt-coral group-hover:scale-110 transition-transform" />
              <span className="font-semibold">07787 814 064</span>
            </a>

            {/* Social Media Icons */}
            <div className="flex items-center gap-3 md:gap-4">
              <a
                href="https://www.instagram.com/kartikpateldentist/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61588647254315"
                target="_blank"
                rel="noopener noreferrer"
                className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation bar */}
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Smile className="w-8 h-8 text-burnt-coral" />
          <div className="flex items-center gap-3">
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
            <span className="text-xl md:text-2xl font-bold text-rabbit">×</span>
            <img 
              src="/perfect-smile-logo.png" 
              alt="Perfect Smile logo" 
              loading="eager"
              className="h-8 md:h-10 w-auto ml-1"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <Link 
            to="/iv-sedation-dentistry"
            className="hidden lg:flex px-3 py-2 text-sm font-medium text-rabbit hover:text-burnt-coral transition-colors"
            data-testid="nav-sedation-link"
          >
            IV Sedation
          </Link>
          <Link 
            to="/pricing"
            className="hidden lg:flex px-3 py-2 text-sm font-medium text-rabbit hover:text-burnt-coral transition-colors"
            data-testid="nav-pricing-link"
          >
            Prices
          </Link>
          <Link 
            to="/about-dr-kartik-patel"
            className="hidden lg:flex px-3 py-2 text-sm font-medium text-rabbit hover:text-burnt-coral transition-colors"
            data-testid="nav-about-link"
          >
            Dr Patel
          </Link>
          <Link 
            to="/contact"
            className="hidden lg:flex px-3 py-2 text-sm font-medium text-rabbit hover:text-burnt-coral transition-colors"
            data-testid="nav-contact-link"
          >
            Contact
          </Link>
          <Link 
            to="/patient-questions"
            className="hidden md:flex px-3 py-2 text-sm font-medium text-rabbit hover:text-burnt-coral transition-colors"
            aria-label="View patient questions and answers"
          >
            Patient Q&A
          </Link>
          <Button 
            variant="outline" 
            size="sm"
            className="hidden md:flex border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white"
            onClick={onServicesScroll}
            aria-label="View our services"
          >
            Services
          </Button>
          <Button 
            size="sm"
            className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"
            onClick={onBooking}
            aria-label="Book an appointment"
          >
            Book Now
          </Button>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 -mr-2 text-rabbit hover:text-burnt-coral transition-colors"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="home-mobile-nav-panel"
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="home-mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown panel */}
      {menuOpen && (
        <div
          id="home-mobile-nav-panel"
          className="lg:hidden border-t border-sky-gray/20 bg-white/98 backdrop-blur-sm shadow-lg animate-in slide-in-from-top-2 duration-200"
          data-testid="home-mobile-nav-panel"
        >
          <nav className="container mx-auto px-4 py-3 flex flex-col" aria-label="Mobile">
            <Link to="/iv-sedation-dentistry" onClick={() => setMenuOpen(false)} className="py-3 text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10" data-testid="home-mobile-nav-sedation-link">IV Sedation</Link>
            <Link to="/pricing" onClick={() => setMenuOpen(false)} className="py-3 text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10" data-testid="home-mobile-nav-pricing-link">Prices</Link>
            <Link to="/about-dr-kartik-patel" onClick={() => setMenuOpen(false)} className="py-3 text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10" data-testid="home-mobile-nav-about-link">Dr Patel</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="py-3 text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10" data-testid="home-mobile-nav-contact-link">Contact</Link>
            <Link to="/patient-questions" onClick={() => setMenuOpen(false)} className="py-3 text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10" data-testid="home-mobile-nav-patient-questions-link">Patient Q&A</Link>
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onServicesScroll && onServicesScroll(); }}
              className="py-3 text-left text-base font-medium text-rabbit hover:text-burnt-coral border-b border-sky-gray/10"
              data-testid="home-mobile-nav-services-link"
            >
              Services
            </button>
            <a href={phoneLink} onClick={() => setMenuOpen(false)} className="py-3 flex items-center gap-2 text-base font-semibold text-burnt-coral hover:text-sky-gray" data-testid="home-mobile-nav-phone-link">
              <Phone className="w-5 h-5" aria-hidden="true" />
              Call 07787 814 064
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
